import React, { useEffect, useState } from "react";
import {
    Building2,
    Save,
    X,
    SquarePen,
} from "lucide-react";
import { postData1 } from "../../../services";
import { toast } from "react-toastify";
import Select from "react-select";

const DetailItem = ({ label, name, value, textarea = false, select = false, options = [], isEditing, handleChange, }) => (
    <div className="pb-5 border-b border-gray-200">

        <p className="text-[14px] font-semibold uppercase text-[#93A1BD] mb-2">
            {label}
        </p>

        {isEditing ? (

            select ? (
                <select
                    name={name}
                    value={value}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2"
                >
                    {options.map((item) => (
                        <option
                            key={item.value}
                            value={item.value}
                        >
                            {item.label}
                        </option>
                    ))}
                </select>
            ) : textarea ? (
                <textarea
                    rows={3}
                    name={name}
                    value={value}
                    onChange={handleChange}
                    className="w-full border rounded-lg p-3"
                />
            ) : (
                <input
                    type="text"
                    name={name}
                    value={value}
                    onChange={handleChange}
                    className="w-full border rounded-lg px-3 py-2"
                />
            )

        ) : (

            <>
                {Array.isArray(value) ? (

                    value.length ? (

                        <div className="flex flex-wrap gap-2">

                            {value.map((item, index) => (

                                <span
                                    key={index}
                                    className="px-3 py-1 text-sm rounded-lg border border-blue-500 bg-blue-50 text-blue-600"
                                >
                                    {item}
                                </span>

                            ))}

                        </div>

                    ) : (

                        <p className="text-[18px] font-medium text-slate-900">
                            -
                        </p>

                    )

                ) : (

                    <p className="text-[18px] font-medium text-slate-900">

                        {name === "profile_type" && value
                            ? value.charAt(0).toUpperCase() +
                            value.slice(1)
                            : value || "-"}

                    </p>

                )}
            </>
        )}
    </div>
);

const BusinessContactDetails = ({
    userData,
    getUserDetails,
}) => {

    const [isEditing, setIsEditing] = useState(false);

    const [formData, setFormData] = useState({
        profile_type: "",
        hospital_name: "",
        speciality: [],
        trade_name: "",
        gstin: "",
        licence_number: "",
        mci_registration_number: "",
        address: "",
    });

    const [specialityOptions, setSpecialityOptions] = useState([]);
    const [selectedSpecialities, setSelectedSpecialities] = useState([]);

    const getSpecialityList = async () => {
        try {

            const res = await postData1("product/speciality/lists/", {}, true, true);

            if (res?.status_code === 200) {

                const options =
                    res?.data?.map((item) => ({
                        value: item.id,
                        label: item.name,
                    })) || [];

                setSpecialityOptions(options);
            }

        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        getSpecialityList();
    }, []);

    useEffect(() => {

        if (specialityOptions.length && userData?.profile?.specialities) {

            const ids = userData.profile.specialities.split(",").map(Number);

            const selected = specialityOptions.filter(item => ids.includes(item.value));

            setSelectedSpecialities(selected);
        }

    }, [specialityOptions, userData]);

    useEffect(() => {
        if (userData) {
            setFormData({
                profile_type: userData?.profile?.profile_type || "",
                hospital_name: userData?.profile?.organization_name || "",
                speciality: userData?.profile?.speciality_names || [],
                trade_name: userData?.profile?.trade_name || "",
                gstin: userData?.profile?.gstin || "",
                licence_number: userData?.profile?.drug_license_number || "",
                mci_registration_number: userData?.profile?.mci_registration_number || "",
                address: userData?.profile?.location || "",
            });
        }
    }, [userData]);

    const handleChange = (e) => {
        setFormData((prev) => ({
            ...prev,
            [e.target.name]: e.target.value,
        }));
    };

    const handleCancel = () => {
        setIsEditing(false);

        setFormData({
            profile_type: userData?.profile?.profile_type || "",
            hospital_name: userData?.profile?.organization_name || "",
            speciality: userData?.profile?.speciality_names || [],
            trade_name: userData?.profile?.trade_name || "",
            gstin: userData?.profile?.gstin || "",
            licence_number: userData?.profile?.drug_license_number || "",
            mci_registration_number: userData?.profile?.mci_registration_number || "",
            address: userData?.profile?.location || "",
        });

        const ids = userData?.profile?.specialities?.split(",")?.map(Number) || [];
        const selected = specialityOptions.filter(item => ids.includes(item.value));
        setSelectedSpecialities(selected);

    };

    const handleSave = async () => {
        try {

            const body = {
                user_id: userData?.id,
                profile_type: formData.profile_type,
                organization_name: formData.hospital_name,
                trade_name: formData.trade_name,
                gstin: formData.gstin,
                drug_license_number: formData.licence_number,
                mci_registration_number: formData.mci_registration_number,
                location: formData.address,
                specialities: selectedSpecialities.map(item => item.value).join(","),
            };

            const res = await postData1(
                "admin/update_profile/",
                body,
                true,
                true
            );

            if (res?.status_code === 200) {

                toast.success(
                    "Business details updated successfully"
                );

                setIsEditing(false);

                getUserDetails &&
                    getUserDetails();

            } else {

                toast.error(
                    res?.msg || "Something went wrong"
                );
            }

        } catch (error) {

            console.log(error);

            toast.error(
                "Failed to update business details"
            );
        }
    };




    return (
        <div className="bg-white rounded-[24px] border border-gray-200 shadow-sm overflow-hidden">

            {/* Header */}

            <div className="flex items-center justify-between px-8 py-6 border-b border-gray-200">

                <div className="flex items-center gap-3">

                    <div className="w-11 h-11 rounded-xl bg-green-100 flex items-center justify-center">

                        <Building2
                            size={20}
                            className="text-green-600"
                        />

                    </div>

                    <h2 className="text-[18px] font-bold uppercase text-slate-800">
                        Business Contact Details
                    </h2>

                </div>

                {!isEditing ? (

                    <button
                        onClick={() => setIsEditing(true)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                    >
                        <SquarePen size={18} />
                    </button>

                ) : (

                    <div className="flex gap-2">

                        <button
                            onClick={handleSave}
                            className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg"
                        >
                            <Save size={16} />
                            Save
                        </button>

                        <button
                            onClick={handleCancel}
                            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg"
                        >
                            <X size={16} />
                            Cancel
                        </button>

                    </div>

                )}

            </div>

            {/* Body */}

            <div className="p-8 space-y-6">

                <DetailItem
                    label="Which Defines You Better : Doctor/Hospital/Distributor"
                    name="profile_type"
                    value={formData.profile_type}
                    select
                    options={[
                        { value: "", label: "Select" },
                        { value: "doctor", label: "Doctor" },
                        { value: "hospital", label: "Hospital" },
                        { value: "distributor", label: "Distributor" },
                    ]}
                    isEditing={isEditing}
                    handleChange={handleChange}
                />

                <DetailItem
                    label="Name of Hospital/Clinic"
                    name="hospital_name"
                    value={formData.hospital_name}
                    isEditing={isEditing}
                    handleChange={handleChange}
                />

                <div className="pb-5 border-b border-gray-200">

                    <p className="text-[14px] font-semibold uppercase text-[#93A1BD] mb-2">
                        Speciality - Multi Speciality (Optional)
                    </p>

                    {isEditing ? (

                        <Select
                            isMulti
                            options={specialityOptions}
                            value={selectedSpecialities}
                            onChange={(selected) =>
                                setSelectedSpecialities(selected || [])
                            }
                            placeholder="Select Specialities"
                        />

                    ) : (

                        <div className="flex flex-wrap gap-2">

                            {formData.speciality?.length ? (

                                formData.speciality.map((item, index) => (
                                    <span key={index} className="px-3 py-1 text-sm rounded-lg border border-blue-500 bg-blue-50 text-blue-600" >
                                        {item}
                                    </span>
                                )
                                )

                            ) : (

                                <span>-</span>

                            )}

                        </div>

                    )}

                </div>

                <DetailItem
                    label="Trade Name"
                    name="trade_name"
                    value={formData.trade_name}
                    isEditing={isEditing}
                    handleChange={handleChange}
                />

                <DetailItem
                    label="GST"
                    name="gstin"
                    value={formData.gstin}
                    isEditing={isEditing}
                    handleChange={handleChange}
                />

                <DetailItem
                    label="Licence Number"
                    name="licence_number"
                    value={formData.licence_number}
                    isEditing={isEditing}
                    handleChange={handleChange}
                />

                <DetailItem
                    label="MCI Registration"
                    name="mci_registration_number"
                    value={formData.mci_registration_number}
                    isEditing={isEditing}
                    handleChange={handleChange}
                />

                <DetailItem
                    label="Address"
                    name="address"
                    value={formData.address}
                    textarea
                    isEditing={isEditing}
                    handleChange={handleChange}
                />

            </div>

        </div>
    );
};

export default BusinessContactDetails;